import { useCallback, useEffect, useRef } from 'react';
import { User } from '@supabase/supabase-js';
import { Properties } from 'posthog-js';
import { usePostHog } from 'posthog-js/react';
import { useSession } from '@supabase/auth-helpers-react';

import type { EHUserAppMetadata } from '@/lib/types';
import { isFromAppStores, isTwaSession } from '@/lib/detection';

/**
 * User events for tracking
 */
export const USER_EVENTS = {
  USER_ONBOARDING_ERROR: 'evt_user_onboarding_error',
  USER_SETTING_NSFW_SWITCH_CLICKED: 'evt_user_stg_nsfw_clicked',
  USER_UPDATE_ERROR: 'evt_user_update_error',
  USER_ERROR: 'evt_user_error',
  USER_DETAILS_ID_NOT_FOUND: 'evt_user_details_id_not_found',
  USER_DEMO_ACCOUNT_LOGIN: 'evt_user_demo_account_login',
  USER_ACCOUNT_DELETION_REQUESTED: 'evt_user_account_deletion_requested',
  USER_ACCOUNT_DELETION_ERROR: 'evt_user_account_deletion_error',
};

/**
 * Payment events for tracking
 */
export const PAYMENT_EVENTS = {
  PAYMENT_CLICKED: 'evt_payment_clicked',
  PAYMENT_COMPLETED: 'evt_payment_completed',
  PAYMENT_CANCELED: 'evt_payment_canceled',
  PAYMENT_ERROR: 'evt_payment_error',
  SUBSCRIPTION_STARTED: 'evt_subscription_started',
  STRIPE_BILLING_PORTAL_OPENED: 'evt_stripe_billing_portal_opened',
  UPGRADE_DIALOG_SHOWN: 'evt_upgrade_dialog_shown',
  UPGRADE_DIALOG_CLICK: 'evt_upgrade_dialog_click',
  GEM_BALANCE_MSG_CLICKED: 'evt_gem_balance_msg_clicked',
};

/**
 * Chat events for tracking
 */
export const CHAT_EVENTS = {
  CHAT_CREATED: 'evt_chat_created',
  CHAT_DELETED: 'evt_chat_deleted',
  CHAT_MSG_SENT: 'evt_chat_msg_sent',
  CHAT_TEXT_MSG_RECEIVED: 'evt_chat_text_msg_received',
  CHAT_IMG_REQUESTED: 'evt_chat_img_requested',
  CHAT_IMG_RECEIVED: 'evt_chat_img_received',
  CHAT_VIDEO_RECEIVED: 'evt_chat_video_received',
  CHAT_ERROR: 'evt_chat_error',
  CHAT_TTS_SUCCESS: 'evt_chat_tts_success',
  CHAT_TTS_ERROR: 'evt_chat_tts_error',
  CHAT_CALL_STARTED: 'evt_chat_call_started',
  CHAT_CALL_ENDED: 'evt_chat_call_ended',
  CHAT_CALL_ERROR: 'evt_chat_call_error',
};

/**
 * Character events for tracking
 */
export const CHARACTER_EVENTS = {
  CHARACTER_FROM_IMG_GENERATED: 'evt_character_from_img_generated',
  NEW_PROFILE_IMAGE_ERROR: 'evt_new_profile_image_error', // error, url_slug
};

export const UI_EVENTS = {
  // include search string in properties
  CHARACTER_TEXT_SEARCH: 'evt_ui_character_text_search',
  // include filter/tag name in properties
  CHARACTER_FILTER_CLICKED: 'evt_ui_character_filter_clicked',
  // include character id, gender, style, age and url_slug in properties
  CHARACTER_CLICKED: 'evt_ui_character_clicked',
  UI_ERROR: 'evt_ui_error',
  SALES_GEMS_DIALOG_SHOWN: 'evt_sales_gems_dialog_shown',
  SALES_GEMS_DIALOG_CLICKED: 'evt_sales_gems_dialog_clicked',
};

export function useTracking() {
  const posthog = usePostHog();
  const session = useSession();
  const userId = useRef(session?.user?.id);

  useEffect(() => { // use a ref to avoid re-rendering
    if (session?.user?.id || userId.current) { // set userId or reset it after logout
      userId.current = session?.user?.id;
    }
  }, [session]);

  const phCapture = useCallback((event: string, properties?: Properties) => {
    posthog.capture(event, properties);
  }, [posthog]);

  /**
   * Track an event
   * @param eventName The name of the event to track
   * @param properties Optional properties to track with the event
   */
  const trackEvent = useCallback((eventName: string, properties?: Properties) => {
    if (!eventName.startsWith('evt_')) {
      console.warn(`Event names should be prefixed with 'evt_': ${eventName}`);
    }

    // Enrich event properties with user ID when available
    if (userId.current) {
      properties = { ...properties, userId: userId.current };
    }

    // Handle subscription started event automatically for now
    // if (eventName === PAYMENT_EVENTS.PAYMENT_COMPLETED) {
    //   trackEvent(PAYMENT_EVENTS.SUBSCRIPTION_STARTED, { ...properties });
    // }
      
    if (import.meta.env.DEV) {
      console.log(`[EVENT] ${eventName}`, properties);
    }
    
    phCapture(eventName, properties);
  }, [phCapture]);
  
  /**
   * Identify user with PostHog
   */
  const phIdentify = useCallback((user: User, properties: Properties = {}) => {
    const {
      user_details_id,
      provider,
    } = user.app_metadata as EHUserAppMetadata;

    const isTwa = isTwaSession();
    const isStoreApp = isFromAppStores();
  
    posthog.identify(
      user.id,
      {
        userDetailsID: user_details_id,
        provider,
        ...(isTwa && { isTwaSession: isTwa }),
        ...(isStoreApp && { isStoreApp }),
        ...properties,
      },
      {
        createdInTwa: isTwa,
        createdInStoreApp: isStoreApp,
      }
    );
  }, [posthog]);

  /**
   * Reset PostHog identity (for logout)
   */
  const phReset = useCallback(() => {
    posthog.reset();
  }, [posthog]);

  return {
    phCapture,
    phIdentify,
    phReset,
    trackEvent
  };
} 